import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

/**
 * Custom time picker that stores values as 24-hour "HH:MM" strings but
 * shows H / M / AM-PM dropdowns. Avoids native <input type="time"> quirks.
 *
 * Props:
 *  - value: string ("HH:MM" or "9:00am" — both accepted)
 *  - onChange(value): receives 24-hour "HH:MM" string (or "" when cleared)
 *  - testIdPrefix: optional, for data-testid attributes
 *  - size: "default" | "sm" (small uses h-8 + text-xs)
 */
export default function TimePicker({ value, onChange, testIdPrefix = "time", size = "default" }) {
  const parsed = parseStored(value);
  const hour = parsed?.h12 || "";
  const minute = parsed?.minute || "";
  const ampm = parsed?.ampm || "";

  const update = (next) => {
    const merged = { h12: hour, minute, ampm, ...next };
    if (!merged.h12 || !merged.minute || !merged.ampm) {
      // Partial — keep showing what's selected but don't emit yet
      // unless ALL three are now empty (clearing)
      if (!merged.h12 && !merged.minute && !merged.ampm) onChange("");
      else if (merged.h12 && merged.minute && merged.ampm) {
        // shouldn't reach here
      }
      // For partial state, store as a temp marker
      // Cleanest: only emit when complete
      return;
    }
    let h = parseInt(merged.h12, 10);
    if (merged.ampm === "PM" && h < 12) h += 12;
    if (merged.ampm === "AM" && h === 12) h = 0;
    const hh = String(h).padStart(2, "0");
    onChange(`${hh}:${merged.minute}`);
  };

  const triggerCls =
    size === "sm"
      ? "bg-zinc-900 border-zinc-800 focus:ring-amber-500 h-8 text-xs px-2"
      : "bg-zinc-950 border-zinc-800 focus:ring-amber-500";

  const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
  const MINUTES = ["00", "15", "30", "45"];

  return (
    <div className="flex items-center gap-1">
      <Select value={hour} onValueChange={(v) => update({ h12: v, ampm: ampm || "AM", minute: minute || "00" })}>
        <SelectTrigger
          data-testid={`${testIdPrefix}-hour`}
          className={`${triggerCls} w-16`}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-72">
          {HOURS.map((h) => (
            <SelectItem key={h} value={h}>{h}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-zinc-500">:</span>
      <Select value={minute} onValueChange={(v) => update({ minute: v, h12: hour || "9", ampm: ampm || "AM" })}>
        <SelectTrigger
          data-testid={`${testIdPrefix}-minute`}
          className={`${triggerCls} w-16`}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          {MINUTES.map((m) => (
            <SelectItem key={m} value={m}>{m}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={ampm} onValueChange={(v) => update({ ampm: v, h12: hour || "9", minute: minute || "00" })}>
        <SelectTrigger
          data-testid={`${testIdPrefix}-ampm`}
          className={`${triggerCls} w-16`}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function parseStored(v) {
  if (!v) return null;
  const s = String(v).trim();
  // 24-hour HH:MM
  let m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const h = parseInt(m[1], 10);
    const ampm = h < 12 ? "AM" : "PM";
    const h12 = String((h % 12) || 12);
    return { h12, minute: m[2], ampm };
  }
  // 12-hour "9:00am"
  m = s.match(/^(\d{1,2}):(\d{2})\s?(am|pm)$/i);
  if (m) {
    return {
      h12: String(parseInt(m[1], 10) || 12),
      minute: m[2],
      ampm: m[3].toUpperCase(),
    };
  }
  return null;
}
